import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import React from 'react'

import { createConfirmation } from '@/app/contexts/confirmation.context'
import { MESSAGE_PERSONALIZATION } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  LocalOpenEnded,
  TWhatsAppButtons,
  TWhatsAppList,
  WhatsAppQuestion,
} from '@/app/module/campaigns/types'
import { WhatsAppTab } from '@/app/module/common/whatsapp/helpers'

import ResponseTextOpenEnded from './response-text-open-ended'
import { AnswerType, ResponseTextProps } from './response-text-props'
import ResponseWhatsappButtons from './response-whatsapp-buttons'
import ResponseWhatsappConfigs from './response-whatsapp-configs'
import { WhatsappConfigs } from './response-whatsapp-configs-utils'
import ResponseWhatsappList from './response-whatsapp-list'
import ResponseWhatsAppSpecific from './response-whatsapp-specific'
import ResponseWhatsappTabs from './response-whatsapp-tabs'
import {
  backToProps,
  checkTabSwitchConfirmation,
  fromProps,
  generateContent,
  hasOpenEndedValidation,
  State,
} from './response-whatsapp-utils'

type Props = ResponseTextProps & {
  item: WhatsAppQuestion
  /**
   * The whatsapp content tab
   */
  tab: WhatsAppTab
  type: CampaignTemplateType // campaign type
  transcribe: boolean
  useAi: boolean

  changeHandler: (item: WhatsAppQuestion) => void
}

export default function ResponseWhatsapp(props: Props) {
  const {
    item,
    id,
    personalizationList,
    senderIds,
    whatsappSenderIds,
    orgId,
    token,
    getImageProps,
    getVoiceProps,
    files = {},
    timezone,
    timezones,
    countryCode,
    countriesTimezones,
    languages,
    defaultLanguage,
    tab,
    transcribe,
    useAi,
    changeHandler,
  } = props

  const [state, setState] = React.useState(fromProps(item))

  React.useEffect(() => {
    setState(fromProps(item))
  }, [item])

  const textPersonalization = React.useMemo(
    () => ({
      ...MESSAGE_PERSONALIZATION,
      ...personalizationList,
    }),
    [personalizationList],
  )

  const handleChange = (newItem: Partial<State>) => {
    const newState = {
      ...state,
      ...newItem,
    }

    const returnToProps = backToProps(newState)

    changeHandler({
      ...item,
      ...returnToProps,
    })

    setState(newState)
  }

  const actionProps = React.useMemo(
    () => ({
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      index: props.index,
      languages,
      personalizationList: textPersonalization,
      senderIds,
      timezone,
      timezones,
      transcribeEnabled: item.transcribe,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    }),
    [
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      item.transcribe,
      languages,
      props.index,
      senderIds,
      textPersonalization,
      timezone,
      timezones,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    ],
  )
  const apiProps = React.useMemo(() => ({ orgId, token }), [orgId, token])

  const handleWAListChange = React.useCallback(
    (value: TWhatsAppList) =>
      changeHandler({
        ...item,
        onList: value,
      }),
    [item, changeHandler],
  )
  const handleWAButtonsChange = React.useCallback(
    (value: TWhatsAppButtons) => changeHandler({ ...item, onButtons: value }),
    [item, changeHandler],
  )
  const handleConfigsChange = React.useCallback(
    (value: WhatsappConfigs) => changeHandler({ ...item, ...value }),
    [item, changeHandler],
  )
  const handleValidationChange = (openEnded: LocalOpenEnded) => {
    const wasEnabled = hasOpenEndedValidation(state.openEnded)
    const isEnabled = hasOpenEndedValidation(openEnded)
    handleChange({
      openEnded,
      ...(!isEnabled || !wasEnabled ? { checkInvalid: false } : {}),
      ...(!isEnabled ? { invalidActions: [] } : {}),
    })
  }

  const configs = React.useMemo(() => {
    if (state.answerType === AnswerType.OpenEnded && !hasOpenEndedValidation(state.openEnded)) {
      return {
        onRetriesExhausted: item.onRetriesExhausted,
        onTimeout: item.onTimeout,
        retries: item.retries,
      }
    }

    return {
      onInvalidReply: item.onInvalidReply,
      onRetriesExhausted: item.onRetriesExhausted,
      onTimeout: item.onTimeout,
      retries: item.retries,
    }
  }, [item.onInvalidReply, item.onRetriesExhausted, item.onTimeout, item.retries, state.answerType, state.openEnded])

  return (
    <div
      className="text-question-block"
      style={{
        marginTop: '2rem',
      }}
    >
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <ResponseWhatsappTabs
        answerType={state.answerType}
        tab={tab}
        onChange={async (val) => {
          const confirmation = checkTabSwitchConfirmation(state, val)
          if (confirmation) {
            const confirmed = await createConfirmation({
              title: confirmation.title,
              content: confirmation.text,
            })

            if (!confirmed) {
              return
            }
          }

          handleChange({
            ...generateContent(val),
            answerType: val,
          })
        }}
      />
      <Divider
        style={{
          margin: '-1px -24px 1rem',
        }}
      />
      {state.answerType === AnswerType.OpenEnded && state.openEnded && (
        <ResponseTextOpenEnded
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={id}
          invalidReplyActions={state.invalidActions}
          openEnded={state.openEnded}
          partType={item.type}
          transcribe={transcribe}
          onChange={handleChange}
          onValidationChange={handleValidationChange}
        />
      )}
      {state.answerType === AnswerType.Text && (
        <ResponseWhatsAppSpecific
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={id}
          onAnswer={state.onAnswer}
          partType={item.type}
          transcribe={transcribe}
          useAi={useAi}
          onChange={handleChange}
        />
      )}
      {state.answerType === AnswerType.WhatsAppList && 'onList' in item && item.onList && (
        <ResponseWhatsappList
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          onList={item.onList}
          onChange={handleWAListChange}
        />
      )}
      {state.answerType === AnswerType.WhatsAppButtons && 'onButtons' in item && item.onButtons && (
        <ResponseWhatsappButtons
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          onButtons={item.onButtons}
          onChange={handleWAButtonsChange}
        />
      )}
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <div style={{ display: 'flex' }}>
        <Typography color="secondary" component="div" variant="subtitle1" gutterBottom={true}>
          <b>Important:</b> If there's no reply within 24 hours, the question will be marked as done, and the survey
          will proceed to the next question (or execute "Failure" actions you specify below). Adjust the timeout in the
          "Timeout / no reply" section below.
        </Typography>
      </div>
      <ResponseWhatsappConfigs
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={props.type}
        configs={configs}
        id={id}
        onChange={handleConfigsChange}
      />
    </div>
  )
}
